import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ObjectId } from '../shared/type-aliases/object-id';
import { IsoString } from '../shared/type-aliases/iso-string';
import { OperationFunction } from '../shared/lists/operation-functions';
import { MoveTypes } from '../shared/lists/move-type';
import { Vehicle } from '../interface/vehicle';
import { CASH_TITLE_CREATED_BY, CASH_TITLE_MOVEMENT_TYPE } from '../interface/cash-log-interfaces';
import { AllInvoiceModels } from '../interface/mkgo-invoice';
import { INVOICE_STATE } from '../shared/lists/invoice-state';
import { NFSE_response } from '../interface/NFSE-response';
import { NF_response } from '../interface/NF-response';
import { CONCILIATION_STATUS } from '@app/interface/conciliation';
import { BOOLEAN_TWO } from '@app/shared/common-enums/boolean-two';
import { TITLE_STATUS } from '@app/interface/cash-title';
import { ILogOS, RO_LOG_ACTION_TYPES } from '@app/interface/os-logs';
import { RO_STATES } from '@app/shared/lists/ro-states';
import { PageEvent } from '@angular/material/paginator';
import { AUTHORIZATION_TYPE_ENUM } from '@app/shared/lists/authorization-types';
import { DIAGWEB_ACTION_TYPE } from '@app/shared/lists/diagweb/diagweb-action-types';
import { CASHIER_EVENT_TYPES } from '@app/interface/log-cashier';
import { InvoiceFlat } from '@app/interface/invoice-flat';
import { PERSON_LOG_ACTION_TYPES, PersonKind } from '@app/interface/person-logs';
import { OPERATION_LOG_ACTION_TYPES } from '@app/interface/operation-logs';
import { PERSON_STATUS_FILTER } from '@app/interface/person';


export interface ApiPaginatedQueryParams {
  page?: number;
  limit?: number;
  /** 0-false 1-true */
  all?: 0 | 1;
  search?: string;

  /**
   *  For OS, the sort is a object with fields to be sorted with a indicator of sortDirection
   * @examples
   *
   * ~~~typescript
   * // sort by field createdAt in decrescent order
   * { createdAt: -1 }
   *
   * // sort by field budgetCode in crescent order
   * { budgetCode: 1 }
   *
   * // sort by field code, then description
   * { code: 1, description: 1 }
   * ~~~
   *
   * for other queries, the sort is a string and the direction of sort is defined into `order` property
   */
  sort?: object | string;

  /**
   *  Order of `sort` (if it is a string)
   *
   * * 1: crescent
   *
   * * -1: decrescent
   *
   */
  order?: 1 | -1;

  /**
   * @deprecated prefer `activeStatus` — `showInactive: true` is equivalent to `activeStatus: 'all'`
   *
   * 'true' to include clients inactive in the current company (default excludes them)
   */
  showInactive?: boolean;

  /** Filters by status in the current company. Defaults to `active` on the API side. */
  activeStatus?: PERSON_STATUS_FILTER;
}

interface ReceiveDateNull {
  receiveDateStart?: IsoString,
  receiveDateEnd?: IsoString,
}

interface ReceiveDateNotNull {
  receiveDateStart: IsoString,
  receiveDateEnd: IsoString,
}
/** Send both or neither */
type ReceiveDate = ReceiveDateNull | ReceiveDateNotNull;

export interface ApiPaginatedPartQueryParams extends ApiPaginatedQueryParams {
  group?: string | string[];
  subgroup?: string;
  subGroup?: string | string[];
  code?: string;
  supplier?: string;
  brand?: string;
  application?: string;
  codeEAN?: string;
  fabricCode?: string;
  location?: string;
  stubCode?: string;
  availableStock?: 0 | 1;
}

export interface ApiPaginatedVehicleQueryParams extends ApiPaginatedQueryParams {
  plate?: string;
  vehicleModel?: string;
  /** the client id */
  client?: string;
  brand?: string;
}

export interface ApiPaginatedVehicleResponse<> {
  vehicles: ApiPaginatedResponse<Vehicle>;
}

interface BasePaginatedOSQueryParams extends ApiPaginatedQueryParams {
  /** last updated time of OS */
  timestamp?: IsoString;
  start?: IsoString,
  end?: IsoString,
  /** Restrict the result to this explicit set of OS ids */
  ids?: string[];
  mechanicQueue?: string;
  mechanic?: string;
  mechanicAssignmentFilter?: 1 | 2 | 3 | 4;
  additionalState?: ObjectId;
  user?: string;
  /** Filter by seller (consultant with priority over creator) — used by profitability report */
  seller?: string;
  client?: string;
  vehicle?: string;
  /** Generic free-text client filter (name / fancyName / clientTMP) */
  clientSearch?: string;
  /** Generic free-text vehicle filter (plate / model / marca / vehicleTMP) */
  vehicleSearch?: string;
  codeSystem?: number;
  budgetCode?: number;
  roType?: ObjectId;
  multipleCodeSystems?: string;
  typeAprovement?: AUTHORIZATION_TYPE_ENUM;
  state?: RO_STATES[];
}

export interface ConciliationPaginatedQueryParams extends ApiPaginatedQueryParams {
  startDate?: IsoString;
  endDate?: IsoString;
  bankCode?: number;
  status?: CONCILIATION_STATUS;
  agency?: string;
  account?: string;
  checkDigit?: string;
}

export interface ApiPaginatedRoLogsQueryParams extends ApiPaginatedQueryParams {
  osId?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  userId?: string;
  reason?: string;
  statusInTheLogRecord?: RO_STATES[];
  actionType?: RO_LOG_ACTION_TYPES[];
}

export interface ApiPaginatedDiagwebLogsQueryParams extends ApiPaginatedQueryParams {
  userId?: string;
  osId?: string;
  company?: string;
  actionType?: DIAGWEB_ACTION_TYPE[];
  bringErrors?: boolean | string;
  createdAtStart?: string;
  createdAtEnd?: string;
}

export interface ApiPersonLogsPaginatedQueryParams extends ApiPaginatedQueryParams {
  personId?: string;
  personKind?: PersonKind;
  userId?: string;
  actionType?: PERSON_LOG_ACTION_TYPES | PERSON_LOG_ACTION_TYPES[];
  createdAtStart?: IsoString;
  createdAtEnd?: IsoString;
}

export interface ApiOperationLogsPaginatedQueryParams extends ApiPaginatedQueryParams {
  operationId?: string;
  userId?: string;
  actionType?: OPERATION_LOG_ACTION_TYPES[];
  createdAtStart?: IsoString;
  createdAtEnd?: IsoString;
}

export type ApiPaginatedOSQueryParams = BasePaginatedOSQueryParams & ReceiveDate & {
  openedDateStart?: IsoString;
  openedDateEnd?: IsoString;
};
interface MovementDateNull {
  movementDateStart?: never,
  movementDateEnd?: never,
}
interface MovementDateNotNull {
  movementDateStart: IsoString,
  movementDateEnd: IsoString,
}
/** Send both or neither */
type MovementDate = MovementDateNull | MovementDateNotNull;

interface CreateDateNull {
  createDateStart?: never,
  createDateEnd?: never,
}
interface CreateDateNotNull {
  /** Date of receive creation, the full title will be returned */
  createDateStart: IsoString,
  /** Date of receive creation, the full title will be returned */
  createDateEnd: IsoString,
}
/** Send both or neither */
type CreateDate = CreateDateNull | CreateDateNotNull;

interface ExpirationDate {
  expirationDateStart?: IsoString,
  expirationDateEnd?: IsoString,
}

/**
 * Expiration date filters accept a start, an end, or a bounded interval.
 */
export interface BaseApiPaginatedTitleQueryParams extends ApiPaginatedQueryParams {
  /**
   * A general text to find by
   *
   * @see invoiceNumber if you are looking for the titles of an especific invoice
   * @see codeSystem if you are looking for the titles of an especific OS
   * @override
   */
  search?: string;
  orderId?: string,
  companyOrder?: string,
  groupId?: string,
  clientSupplier?: ObjectId,
  type?: ObjectId[];
  expenseType?: ObjectId[];
  /**
   * `gt` Titles with balance greather than zero
   * `eq` Titles without balance
   */
  balance?: ['gt'] | ['eq'] | ['gt', 'eq'] | [];

  /**
   * Filters by the title's **situation**, classified solely by the back-end (same rule that fills
   * the per-document `situation` field and the summary aggregates), including the title with no
   * due date, which is `upcoming`.
   *
   * `open` ("Em aberto") is a **filter-only** value: every title that still has a balance, i.e. the
   * union of `overdue`/`dueToday`/`upcoming`. It is never a value of the per-document `situation`
   * field nor a bucket of the summary aggregates, and it derives no due-date condition at all.
   *
   * Independent from {@link balance}: sending both applies both conditions — `open` together with
   * `['eq']` is contradictory and returns nothing, rather than one overriding the other.
   */
  situation?: 'overdue' | 'dueToday' | 'upcoming' | 'settled' | 'open';

  /** codeSystem of OS */
  companyCode?: string

  /** Number of the invoice that creted the title (detached) */
  invoiceNumber?: number;
  paymentCondition?: ObjectId;
  valueStart?: number;
  valueEnd?: number;
  /**
   * 3 - Active and canceled
   */
  titleStatus?: TITLE_STATUS.ACTIVE | TITLE_STATUS.CANCELED | 3;
  statusTitle?: ObjectId;

  boleto?: boolean;

  /** Multiple client/supplier filter mode */
  clientSupplierMode?: 'all' | 'include' | 'exclude';
  /** Array of client/supplier IDs for multiple selection */
  clientSupplierIds?: string[];

  observation?: string;

  /**
   * Opt-in flag for the summary aggregates (KPIs by due situation, counts by type and the
   * per-document `situation` field). Only the new titles panel sends it; without it the
   * response is byte-for-byte the current one.
   */
  summary?: 0 | 1;

  /** Filters titles that have a low (`received.bank`) on the given bank/account (ObjectId). */
  receivedBank?: string;

  /** ObjectId of the originating OS. Wins over `companyCode` when both are sent (D4/Phase 6). */
  idOS?: string;
  /** Id of the invoice (NF/NFSe) that stamped the title (Phase B). Strong key, plain equality. */
  invoiceId?: string;
  /** Serial of the invoice. Paired with `invoiceNumber` for the detached-invoice lookup (I-1/I-11). */
  serial?: string;
  /**
   * Opt-in that lifts the `status: { $ne: 0 }` floor — the API rejects it unless `invoiceNumber`
   * is also sent. Scoped to the detached-invoice lookup (D7/I-11); do not use it elsewhere.
   */
  includeInactive?: 0 | 1;
}

export type ApiPaginatedTitleQueryParams = BaseApiPaginatedTitleQueryParams & MovementDate & CreateDate & ExpirationDate;

export interface ApiPaginatedPartsQueryParams extends ApiPaginatedQueryParams {
  /**
   * 1 - bigger than
   * 2 - less than
   * 3 - equal to
   */
  stockCondition?: 1 | 2 | 3;
}

export enum INVOICE_LOG_BRING_ERRORS {
  DO_NOT_BRING = 1,
  ONLY_BRING_ERRORS = 2,
  BRING_ALL_LOGS = 3
}

export interface ApiPaginatedInvoiceLogsQueryParams extends ApiPaginatedQueryParams {
  osId?: string;
  orderId?: string;
  invoiceId?: string;
  invoiceNumber?: string;
  userId?: string;
  stateBeforeChanges?: string[];
  newState?: string[];
  actionType?: string[];
  bringErrors?: INVOICE_LOG_BRING_ERRORS;
  attemptId?: string;
  stage?: string;
  source?: string;
  outcome?: string;
}

export interface ApiPaginatedMovementQueryParams extends ApiPaginatedQueryParams {
  moveType?: MoveTypes;
  functionType?: OperationFunction;
  /** 1 -> greater than 0
   *  0 -> lower than 0
   */
  balance?: 1 | 0;
  item?: string;
  startDate?: IsoString;
  endDate?: IsoString;
  sortField?: string;
  direction?: "asc" | "desc";
  isBalanceAboveZero?: 1 | 0;
  groups?: string[];
  subgroups?: string[];
  bringErrors: BOOLEAN_TWO;
}

export interface ApiCashierLogsPaginatedQueryParams extends ApiPaginatedQueryParams {
  userId?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  openingDateStart?: string;
  openingDateEnd?: string;
  closingDateStart?: string;
  closingDateEnd?: string;

  closingValue?: number;
  openingValue?: number;
  valueChanged?: number;

  eventType?: CASHIER_EVENT_TYPES[];
}

export interface ApiCashierLogsPaginatedQueryParams extends ApiPaginatedQueryParams {
  userId?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  openingDateStart?: string;
  openingDateEnd?: string;
  closingDateStart?: string;
  closingDateEnd?: string;

  closingValue?: number;
  openingValue?: number;
  valueChanged?: number;

  eventType?: CASHIER_EVENT_TYPES[];
}

export interface ApiTitleLogsPaginatedQueryParams extends ApiPaginatedQueryParams {
  osId?: string;
  orderId?: string;
  invoiceId?: string;
  invoiceNumber?: string | number;
  userId?: string;
  titleId?: string;
  clientId?: string;
  supplierId?: string;
  createdBy?: CASH_TITLE_CREATED_BY;
  movementType?: CASH_TITLE_MOVEMENT_TYPE;
  createdAtStart?: string;
  createdAtEnd?: string;
}

export interface ApiBoletoLogsPaginatedQueryParams extends ApiPaginatedQueryParams {
  boleto_id?: string;
  action?: string;
  source?: string;
  user_id?: string;
  operation_id?: string;
  company_id?: string;
  os_id?: string;
  title_id?: string;
}

export interface ApiPaginatedResponse<datatype> {
  docs: Array<datatype>,
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  nextPage: any;
  page: number;
  pagingCounter: number;
  prevPage: any;
  totalDocs: number;
  totalPages: number;
}

export interface ApiVehiclePaginatedResponse<Vehicle> {
  vehicles: ApiPaginatedResponse<Vehicle>
}

export interface ApiPaginatedTitlesResponse<datatype> extends ApiPaginatedResponse<datatype> {
  totals?: {
    balancePayTotal: number;
    balanceReceiveTotal: number;
    paidTotal: number;
    receivedTotal: number;
    valuePayTotal: number;
    valueReceiveTotal: number;
    _id: ObjectId;
  }
}

export type PaginatedInvoice = InvoiceFlat;

export interface ApiPaginatedInvoiceResponse<T extends PaginatedInvoice | NFSE_response | NF_response> {
  invoices: T[];
  pagination: {
    hasNext: boolean;
    hasPrev: boolean;
    limit: number;
    total: number;
    page: number;
    totalPages: number
  }
}

export interface ApiPaginatedInvoiceQueryParams {
  clientOrSupplierName?: string;
  clientOrSupplierDocument?: string;
  clientId?: string;
  supplierId?: string;
  numero?: string;
  serie?: string;
  osNumber?: string | number;
  orderNumber?: string | number;
  osId?: string;
  orderID?: string
  /**
  * 1 - only entry
  * 2 - only out
  * 3 - both of them
  */
  isEntry?: boolean;
  /**
  * 1 - only fiscal
  * 2 - only gerencial
  * 3 - both of them
  */
  isFiscal?: boolean;
  invoiceState?: INVOICE_STATE[];
  model?: AllInvoiceModels[];
  emitionDateStart?: string;
  emitionDateEnd?: string;
  timezone?: string;
  devolutionOriginInvoiceId?: string;
  page?: number;
  limit?: number;
  all?: 0 | 1;
  /**
   * @deprecated
   * Not working
   * @todo remove
   */
  search?: string;
  orderBy?: object | string;
  order?: 1 | -1;
}

interface ModuleData<dataType = any> {
  cache: Map<string, dataType>;
  timestamp: number;
}

type ModuleMap = Map<string, ModuleData>;

type CompanyMap = Map<string, ModuleMap>;

@Injectable({
  providedIn: 'root'
})
export class PaginationService {

  private _companyMap: CompanyMap = new Map();

  constructor() { }

  getData<dataType>(companyId: string, moduleId: string): ModuleData<dataType> | undefined {
    const moduleMap = this._companyMap.get(companyId);
    if (moduleMap) {
      const moduleData = moduleMap.get(moduleId);
      if (moduleData) {
        return moduleData;
      }
    }
    return {
      cache: new Map<string, dataType>(),
      timestamp: 0
    }
  }

  setData(companyId: string, moduleId: string, data: ModuleData) {
    const moduleMap: ModuleMap = this._companyMap.get(companyId) || new Map();
    const moduleData = moduleMap.get(moduleId) || { cache: new Map(), timestamp: 0 };
    moduleData.cache = this._mergeMaps(moduleData.cache, data.cache);
    moduleData.timestamp = data.timestamp;
    moduleMap.set(moduleId, moduleData);
    this._companyMap.set(companyId, moduleMap);
  }

  /** A helper for config the HttpParams request */
  static getParams(options: ApiPaginatedQueryParams | object): HttpParams {
    let params = new HttpParams();
    if (options) {
      const keys = Object.keys(options);
      for (const key of keys) {
        switch (key) {
          case "all":
          case "limit":
          case "page":
            const value = options[key];
            if (typeof value === 'string') {
              params = params.append(key, value)
            } else if (Number.isFinite(value)) {
              params = params.append(key, `${value}`);
            }
            break;
          case "timestamp":
          case "search":
            if (options[key]) {
              params = params.append(key, `${options[key]}`);
            }
            break;
          case "state":
            params = params.append(key, `[${options[key]}]`);
            break;
          case "vehicleModel":
            params = params.append("vehicleModel", options[key]);
            break;
          case "model":
            params = params.append(key, `[${options[key].map((model: string) => `"${model}"`)}]`);
            break;
          case "invoiceState":
            params = params.append("state", `[${options[key]}]`);
            break;
          case "sort":
            if (keys.includes('order')) {
              // here, sort is a string
              params = params.append(key, `${options[key]}`);
            } else {
              // here, sort is a object
              params = params.append(key, JSON.stringify(options[key]))
            }
            break;
          case "stateBeforeChanges":
          case "newState":
          case "actionType":
            if (options[key] && Array.isArray(options[key]) && options[key].length > 0) {
              params = params.append(key, options[key].join(","));
            }
            break;
          default:
            if (options[key] !== undefined && options[key] !== null) {
              params = params.append(key, `${options[key]}`);
            }
            break;
        }
      }
    }
    return params;
  }

  private _mergeMaps(...maps: Map<string, any>[]) {
    const newMap = new Map();
    maps.forEach(m => {
      for (const key of Array.from(m.keys())) {
        newMap.set(key, m.get(key));
      }
    });
    return newMap;
  }

  static notFoundErrorDataToReturn<T>(): ApiPaginatedResponse<T | any> {
    return {
      docs: [],
      hasNextPage: false,
      hasPrevPage: false,
      limit: 0,
      nextPage: null,
      page: 0,
      pagingCounter: 0,
      prevPage: null,
      totalDocs: 0,
      totalPages: 0
    }
  }
}
